import { TemplateElement, html, defineElement, when, unsafeHTML } from '@webtides/element-js';
import i18n from '../../utils/i18n.js';
import style from './cookie-consent-banner.css';
import '../icon/icon.js';

/**
 * @property {string} text
 * @property {string} buttonLabel
 * @property {string} cookieConsentGroup
 * @property {boolean} showBanner
 * @property {boolean} onlyCh
 */
export default class CookieConsentBanner extends TemplateElement {
    constructor() {
        super({ styles: [style], shadowRender: true });

        this._geoResolved = false;
        this._isCH = false;
    }

    properties() {
        return {
            text: '',
            buttonLabel: '',
            cookieConsentGroup: 'C0001',
            showBanner: true,
            onlyCh: false,
        };
    }

    get hasConsented() {
        return window.OptanonActiveGroups && window.OptanonActiveGroups.includes(this.cookieConsentGroup);
    }

    get shouldRenderContent() {
        if (this.onlyCh) {
            if (!this._geoResolved) return false;
            return this._isCH;
        }
        return true;
    }

    connected() {
        if (this.hasAttribute('text') && this.hasAttribute('button-label')) return;

        // we don't need to show anything for C0001
        if (this.cookieConsentGroup === 'C0001') {
            this.showBanner = false;
            return;
        }

        // set default message
        this.text = i18n('cookieConsentOverlayMessages.defaultText');

        // hide until geo resolves when only-ch is set
        if (this.onlyCh) this.showBanner = false;

        window.optanonReady().then(() => {
            const geolocationData = window.Optanon?.getGeolocationData();
            this._isCH = geolocationData?.country === 'CH';
            this._geoResolved = true;

            if (this._isCH) {
                this.text = i18n(`cookieConsentOverlayMessages.${this.cookieConsentGroup}.text`);
                this.buttonLabel = i18n(`cookieConsentOverlayMessages.${this.cookieConsentGroup}.buttonLabel`);
            }

            if (!this.shouldRenderContent) {
                this.showBanner = false;
                return;
            }

            this.showBanner = !this.hasConsented;
        });
    }

    events() {
        return {
            window: {
                OneTrustGroupsUpdated: () => {
                    if (!this.shouldRenderContent) {
                        this.showBanner = false;
                        return;
                    }
                    this.showBanner = !this.hasConsented;
                },
            },
            '[data-consent]': {
                click: () => {
                    window.Optanon?.UpdateConsent('Category', `${this.cookieConsentGroup}:1`);
                },
            },
        };
    }

    template() {
        if (!this.showBanner) {
            return html`<slot></slot>`;
        }

        const decodedText = new DOMParser().parseFromString(this.text, 'text/html').documentElement.textContent;

        return html`
            <div class="relative aspect-video min-h-[250px]">
                <div class="absolute inset-0 h-full w-full bg-gray-20 grid place-items-center z-[12]">
                    <div class="content bg-[white] p-16 md:p-24 rounded-xs">
                        <div class="richtext" style="margin-bottom: 1rem;">${unsafeHTML(decodedText)}</div>
                        ${when(
                            this.buttonLabel && this.cookieConsentGroup,
                            html`<button class="btn btn-primary" data-consent>${this.buttonLabel}</button>`,
                        )}
                    </div>
                </div>
            </div>
        `;
    }
}

defineElement('mb-cookie-consent-banner', CookieConsentBanner);
